import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  makeStyles,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import SearchIcon from '@material-ui/icons/Search'
import chatApi from '../../api/chatApi'
import { Constructors_ClinicalDiagnosis } from '../../api/chatApi/chatApi'
import useHasDifferentiatedDiagnosis from '../../hooks/useHasDifferentiatedDiagnosis'

type DiagnosisVariant = {
  id?: number
  code?: string
  name?: string
}

type DiffDiagnosisOption = {
  id: number
  label: string
}

type AssignedDiffDiagnosis = {
  id: number
  label: string
  userDiagnosisRelationId?: number
  exist?: boolean
}

type DiagnosisColumn = {
  id: number
  diagnosisId: number
  diagnosisLabel: string
  userDiagnosisId?: number
  diffItems: AssignedDiffDiagnosis[]
}

const MKB_PAGE_SIZE = 30

const useStyles = makeStyles(() =>
  createStyles({
    triggerWrap: {
      padding: '8px 24px 0',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 16,
    },
    field: {
      flex: 1,
      minWidth: 0,
    },
    addButton: {
      height: 40,
      whiteSpace: 'nowrap',
    },
    stickySearchHeader: {
      backgroundColor: '#fff',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      paddingTop: 8,
      paddingBottom: 8,
    },
    columnsWrap: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 12,
      marginTop: 16,
    },
    column: {
      minHeight: 180,
      border: '1px solid #dcdcdc',
      borderRadius: 6,
      backgroundColor: '#fafafa',
      padding: 10,
    },
    columnDragOver: {
      border: '1px solid #1976d2',
      backgroundColor: '#eef5ff',
    },
    columnTitle: {
      fontWeight: 600,
      borderBottom: '1px solid #e0e0e0',
      paddingBottom: 8,
      marginBottom: 8,
      wordBreak: 'break-word',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    diffItem: {
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      padding: '6px 8px',
      backgroundColor: '#fff',
      marginBottom: 6,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 8,
      fontSize: 13,
    },
    actionsWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      marginLeft: 'auto',
    },
    diffTabsWrap: {
      marginTop: 8,
      border: '1px solid #e0e0e0',
      borderRadius: 6,
      backgroundColor: '#fff',
      padding: 10,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      minHeight: 48,
    },
    diffSectionTitle: {
      marginTop: 16,
      fontWeight: 600,
    },
    draggableCard: {
      marginTop: 10,
      display: 'inline-flex',
      alignItems: 'center',
      padding: '8px 10px',
      border: '1px dashed #1976d2',
      borderRadius: 4,
      backgroundColor: '#fff',
      cursor: 'grab',
      userSelect: 'none',
      maxWidth: '100%',
      wordBreak: 'break-word',
    },
    draggableCardDisabled: {
      cursor: 'default',
      opacity: 0.55,
      border: '1px dashed #bdbdbd',
      color: '#9e9e9e',
    },
    helperText: {
      marginTop: 8,
      color: '#666',
      fontSize: 12,
    },
  })
)

function containsText(text: string | undefined, query: string): boolean {
  if (!query.trim()) return true
  return (text || '').toLowerCase().includes(query.toLowerCase())
}

function parseMkbSearch(searchValue: string): { text: string; code: string } {
  const query = (searchValue || '').trim()
  if (!query) return { text: '', code: '' }
  const looksLikeCode = /^[A-Za-z][A-Za-z0-9.]*$/.test(query) && /\d/.test(query)
  if (looksLikeCode) return { text: '', code: query }
  return { text: query, code: '' }
}

function stopSearchFieldKeys(event: React.KeyboardEvent) {
  if (event.key !== 'Escape') {
    event.stopPropagation()
  }
}

function mapDiffOptions(response: any): DiffDiagnosisOption[] {
  const rows = response?.result || []
  const byId = new Map<number, string>()
  rows.forEach((item: any) => {
    const id = Number(item?.differentiatedDiagnosis?.id || 0)
    const label = String(item?.differentiatedDiagnosis?.differentiatedDiagnosis || '').trim()
    if (!id || !label) return
    if (byId.has(id)) return
    byId.set(id, label)
  })
  return Array.from(byId.entries()).map(([id, label]) => ({ id, label }))
}

function formatDiagnosisLabelFromMedical(medical: any, fallback: string): string {
  const name = String(medical?.name || '').trim()
  const code = String(medical?.code || '').trim()
  if (!name && !code) return fallback
  if (code && name) return `${code} - ${name}`
  return name || code || fallback
}

function getCurrentTaskUserDiagnoses(response: any): any[] {
  const nested = response?.result?.result
  if (Array.isArray(nested)) return nested
  const direct = response?.result
  if (Array.isArray(direct)) return direct
  return []
}

function getUserDiagnosisRelations(response: any): any[] {
  const direct = response?.result
  if (Array.isArray(direct)) return direct
  const nested = response?.result?.result
  if (Array.isArray(nested)) return nested
  return []
}

export default function DifferentialDiagnosisDialog() {
  const classes = useStyles()
  const hasDifferentiatedDiagnosis = useHasDifferentiatedDiagnosis()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState(0)
  const [showCheckResult, setShowCheckResult] = useState(false)

  const [mkbSearch, setMkbSearch] = useState('')
  const [mkbOptions, setMkbOptions] = useState<DiagnosisVariant[]>([])
  const [mkbPage, setMkbPage] = useState(1)
  const [mkbHasMore, setMkbHasMore] = useState(true)
  const [mkbLoading, setMkbLoading] = useState(false)
  const mkbLoadingRef = useRef(false)
  const mkbRequestIdRef = useRef(0)
  const mkbSearchInputRef = useRef<HTMLInputElement | null>(null)
  const clinicalSearchInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedMkbId, setSelectedMkbId] = useState<number | ''>('')
  const [selectedMkbLabel, setSelectedMkbLabel] = useState('')

  const [clinicalSearch, setClinicalSearch] = useState('')
  const [clinicalOptions, setClinicalOptions] = useState<Array<Constructors_ClinicalDiagnosis>>([])
  const [selectedClinicalId, setSelectedClinicalId] = useState<number | ''>('')
  const [selectedClinicalLabel, setSelectedClinicalLabel] = useState('')

  const [diffDiagnosisOptions, setDiffDiagnosisOptions] = useState<DiffDiagnosisOption[]>([])

  const [mkbColumns, setMkbColumns] = useState<DiagnosisColumn[]>([])
  const [clinicalColumns, setClinicalColumns] = useState<DiagnosisColumn[]>([])

  const [dragOverColumnId, setDragOverColumnId] = useState<number | null>(null)

  const currentType: 1 | 2 = tab === 0 ? 1 : 2
  const currentColumns = tab === 0 ? mkbColumns : clinicalColumns

  const assignedDiffIds = useMemo(() => {
    const ids = currentColumns.flatMap(column => column.diffItems.map(item => item.id))
    return new Set(ids)
  }, [currentColumns])

  const availableDiffOptions = useMemo(
    () => diffDiagnosisOptions.filter(option => !assignedDiffIds.has(option.id)),
    [diffDiagnosisOptions, assignedDiffIds]
  )

  const filteredClinical = useMemo(
    () =>
      clinicalOptions.filter(
        item => containsText(item.name, clinicalSearch) || containsText(item.value, clinicalSearch)
      ),
    [clinicalOptions, clinicalSearch]
  )

  const canAddDiagnosis = useMemo(() => {
    if (currentColumns.length >= 3) return false
    if (currentType === 1) return Boolean(selectedMkbId)
    return Boolean(selectedClinicalId)
  }, [currentColumns.length, currentType, selectedMkbId, selectedClinicalId])

  const canCheck = currentColumns.some(column => column.diffItems.length > 0)
  const canDragSymptoms = currentColumns.length >= 2

  const fetchMkb = useCallback(async (pageToLoad: number, resetList: boolean, searchValue: string) => {
    if (!resetList && mkbLoadingRef.current) return

    const requestId = ++mkbRequestIdRef.current
    mkbLoadingRef.current = true
    setMkbLoading(true)
    try {
      const { text, code } = parseMkbSearch(searchValue)
      const response = await chatApi.getDiagnosisOptionsVariantes(
        'IL4',
        text,
        code,
        pageToLoad,
        MKB_PAGE_SIZE
      )
      if (requestId !== mkbRequestIdRef.current) return
      const results = response?.result?.results || []
      const maxPages = Number(response?.result?.maxPages || 0)
      setMkbOptions(prev => (resetList ? results : [...prev, ...results]))
      setMkbPage(pageToLoad)
      setMkbHasMore(pageToLoad < maxPages)
    } catch (error) {
      if (requestId !== mkbRequestIdRef.current) return
      console.log('Ошибка загрузки диагнозов по МКБ', error)
      if (resetList) {
        setMkbOptions([])
      }
      setMkbHasMore(false)
    } finally {
      if (requestId === mkbRequestIdRef.current) {
        mkbLoadingRef.current = false
        setMkbLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const timeoutId = setTimeout(() => {
      fetchMkb(1, true, mkbSearch)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [mkbSearch, open, fetchMkb])

  useEffect(() => {
    if (!open) return
    setShowCheckResult(false)

    const loadClinicalAndDiff = async (): Promise<Array<Constructors_ClinicalDiagnosis>> => {
      try {
        const [clinicalResponse, diffResponse] = await Promise.all([
          chatApi.getConstructorsClinicalDiagnosisAll(),
          chatApi.getDifDiagnosisCurrentTask(),
        ])
        const clinicalList = clinicalResponse?.result || []
        setClinicalOptions(clinicalList)
        setDiffDiagnosisOptions(mapDiffOptions(diffResponse))
        return clinicalList
      } catch (error) {
        console.log('Ошибка загрузки справочников Симптома', error)
        setClinicalOptions([])
        setDiffDiagnosisOptions([])
        return []
      }
    }

    const loadSavedDiagnoses = async (clinicalList: Array<Constructors_ClinicalDiagnosis>) => {
      try {
        const [diagnosesResponse, relationsResponse] = await Promise.all([
          chatApi.getDifDiagnosisCurrentTaskUser(),
          chatApi.getDifDiagnosisUserDiagnosisAll(),
        ])

        const diagnoses = getCurrentTaskUserDiagnoses(diagnosesResponse)
        const relationRows = getUserDiagnosisRelations(relationsResponse)
        const mkb: DiagnosisColumn[] = []
        const clinical: DiagnosisColumn[] = []

        const relationsByUserDiagnosisId: Record<number, AssignedDiffDiagnosis[]> = {}
        relationRows.forEach((item: any) => {
          const userDiagnosisId = Number(item?.differentiatedDiagnosisDiagnosisUserId || item?.differentiatedDiagnosisDiagnosisUser?.id || 0)
          const diffId = Number(item?.differentiatedDiagnosis?.id || item?.differentiatedDiagnosisId || 0)
          const diffLabel = String(item?.differentiatedDiagnosis?.differentiatedDiagnosis || '').trim()
          if (!userDiagnosisId || !diffId || !diffLabel) return

          if (!relationsByUserDiagnosisId[userDiagnosisId]) relationsByUserDiagnosisId[userDiagnosisId] = []
          const alreadyAdded = relationsByUserDiagnosisId[userDiagnosisId].some(existing => existing.id === diffId)
          if (alreadyAdded) return

          relationsByUserDiagnosisId[userDiagnosisId].push({
            id: diffId,
            label: diffLabel,
            userDiagnosisRelationId: Number(item?.id || 0) || undefined,
            exist: typeof item?.exist === 'boolean' ? item.exist : undefined,
          })
        })

        diagnoses.forEach((diag: any) => {
          const userDiagnosisId = Number(diag?.id || 0)
          const diagnosisType = Number(diag?.type || 0) as 1 | 2
          const diagnosisId = Number(diag?.diagnosisId || 0)
          if (!userDiagnosisId || !diagnosisId || (diagnosisType !== 1 && diagnosisType !== 2)) return

          const fallback =
            diagnosisType === 2
              ? (clinicalList.find(row => Number(row.id) === diagnosisId)?.name || `Клинический диагноз #${diagnosisId}`)
              : `МКБ диагноз #${diagnosisId}`

          const column: DiagnosisColumn = {
            id: Date.now() + userDiagnosisId,
            diagnosisId,
            diagnosisLabel: formatDiagnosisLabelFromMedical(diag?.medical, fallback),
            userDiagnosisId,
            diffItems: relationsByUserDiagnosisId[userDiagnosisId] || [],
          }

          if (diagnosisType === 1) mkb.push(column)
          if (diagnosisType === 2) clinical.push(column)
        })

        setMkbColumns(mkb.slice(0, 3))
        setClinicalColumns(clinical.slice(0, 3))
      } catch (error) {
        console.log('Ошибка загрузки сохраненных диагнозов пользователя', error)
      }
    }

    const loadAll = async () => {
      const clinicalList = await loadClinicalAndDiff()
      await loadSavedDiagnoses(clinicalList)
    }

    loadAll()
  }, [open])

  const handleMkbMenuScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.currentTarget
    const isBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 20
    if (isBottom && mkbHasMore && !mkbLoading) {
      fetchMkb(mkbPage + 1, false, mkbSearch)
    }
  }

  const focusSearchInput = (inputRef: React.MutableRefObject<HTMLInputElement | null>) => {
    window.setTimeout(() => {
      inputRef.current?.focus()
    }, 10)
  }

  const searchMenuProps = {
    autoFocus: false,
    disableAutoFocusItem: true,
    disableEnforceFocus: true,
    getContentAnchorEl: null as null,
    MenuListProps: {
      autoFocusItem: false,
      style: { backgroundColor: '#fff' },
    },
  }

  const updateCurrentColumns = (updater: (prev: DiagnosisColumn[]) => DiagnosisColumn[]) => {
    if (currentType === 1) {
      setMkbColumns(prev => updater(prev))
    } else {
      setClinicalColumns(prev => updater(prev))
    }
  }

  const handleAddDiagnosis = async () => {
    if (!canAddDiagnosis) return

    const diagnosisId = currentType === 1 ? Number(selectedMkbId) : Number(selectedClinicalId)
    const diagnosisLabel = currentType === 1 ? selectedMkbLabel : selectedClinicalLabel

    if (!diagnosisId || !diagnosisLabel) return
    if (currentColumns.some(column => column.diagnosisId === diagnosisId)) return

    try {
      const response = await chatApi.createDifDiagnosiskUser({
        diagnosisId,
        type: currentType,
      })
      const userDiagnosisId = Number((response as any)?.result?.id || 0) || undefined

      updateCurrentColumns(prev => [
        ...prev,
        {
          id: Date.now(),
          diagnosisId,
          diagnosisLabel,
          userDiagnosisId,
          diffItems: [],
        },
      ])
    } catch (error) {
      console.log('Ошибка сохранения диагноза пользователя', error)
      return
    }

    if (currentType === 1) {
      setSelectedMkbId('')
      setSelectedMkbLabel('')
    } else {
      setSelectedClinicalId('')
      setSelectedClinicalLabel('')
    }
  }

  const handleDropToColumn = (columnId: number, diffId: number) => {
    if (currentColumns.length < 2) return
    const selectedDiffOption = diffDiagnosisOptions.find(option => option.id === diffId)
    if (!selectedDiffOption) return
    if (assignedDiffIds.has(selectedDiffOption.id)) return

    const targetColumn = currentColumns.find(column => column.id === columnId)
    if (!targetColumn?.userDiagnosisId) return

    const createRelation = async () => {
      try {
        const createResponse = await chatApi.createDifDiagnosisUserDiagnosis({
          differentiatedDiagnosisDiagnosisUserId: targetColumn.userDiagnosisId,
          differentiatedDiagnosisId: selectedDiffOption.id,
        })
        const relationId = Number(createResponse?.result?.id || 0) || undefined

        updateCurrentColumns(prev =>
          prev.map(column =>
            column.id === columnId
              ? {
                  ...column,
                  diffItems: [
                    ...column.diffItems,
                    {
                      id: selectedDiffOption.id,
                      label: selectedDiffOption.label,
                      userDiagnosisRelationId: relationId,
                    },
                  ],
                }
              : column
          )
        )
        setDragOverColumnId(null)
      } catch (error) {
        console.log('Ошибка создания связи Симптома', error)
      }
    }

    createRelation()
  }

  const removeDiagnosisColumn = async (columnId: number) => {
    const columnToRemove = currentColumns.find(column => column.id === columnId)
    if (!columnToRemove) return

    if (columnToRemove.userDiagnosisId) {
      try {
        await chatApi.deleteDifDiagnosisUser(columnToRemove.userDiagnosisId)
      } catch (error) {
        console.log('Ошибка удаления диагноза пользователя', error)
        return
      }
    }

    updateCurrentColumns(prev => prev.filter(column => column.id !== columnId))
  }

  const removeDiffFromColumn = async (columnId: number, diffId: number) => {
    const column = currentColumns.find(item => item.id === columnId)
    const diff = column?.diffItems.find(item => item.id === diffId)
    if (!column || !diff) return

    if (diff.userDiagnosisRelationId) {
      try {
        await chatApi.deleteDifDiagnosisUserDiagnosis(diff.userDiagnosisRelationId)
      } catch (error) {
        console.log('Ошибка удаления связи Симптома', error)
        return
      }
    }

    updateCurrentColumns(prev =>
      prev.map(item =>
        item.id === columnId
          ? { ...item, diffItems: item.diffItems.filter(diag => diag.id !== diffId) }
          : item
      )
    )
  }

  const handleCheck = async () => {
    try {
      const relationsResponse = await chatApi.getDifDiagnosisUserDiagnosisAll()
      const rows = getUserDiagnosisRelations(relationsResponse)
      const existByRelationId = new Map<number, boolean>()
      rows.forEach((item: any) => {
        const relationId = Number(item?.id || 0)
        if (!relationId) return
        if (typeof item?.exist !== 'boolean') return
        existByRelationId.set(relationId, item.exist)
      })

      updateCurrentColumns(prev =>
        prev.map(column => ({
          ...column,
          diffItems: column.diffItems.map(item =>
            item.userDiagnosisRelationId && existByRelationId.has(item.userDiagnosisRelationId)
              ? { ...item, exist: existByRelationId.get(item.userDiagnosisRelationId) }
              : item
          ),
        }))
      )
      setShowCheckResult(true)
    } catch (error) {
      console.log('Ошибка проверки Симптома', error)
    }
  }

  if (!hasDifferentiatedDiagnosis) return null

  return (
    <>
      <div className={classes.triggerWrap}>

         {/* Симптом вернуть*/}
         <Button variant='outlined' color='primary' fullWidth onClick={() => setOpen(true)}>
          Симптом
        </Button>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='lg'
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>Симптом</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} indicatorColor='primary' textColor='primary'>
            <Tab label='Диагноз по МКБ' />
            <Tab label='Клинический диагноз' />
          </Tabs>

          <div className={classes.row}>
            {tab === 0 ? (
              <FormControl variant='outlined' className={classes.field} fullWidth>
                <InputLabel>Диагноз по МКБ</InputLabel>
                <Select
                  label='Диагноз по МКБ'
                  value={selectedMkbId}
                  onOpen={() => focusSearchInput(mkbSearchInputRef)}
                  onChange={(event) => {
                    const id = Number(event.target.value)
                    const found = mkbOptions.find(option => Number(option.id) === id)
                    setSelectedMkbId(id || '')
                    setSelectedMkbLabel(found?.code ? `${found.code} - ${found.name || ''}` : (found?.name || ''))
                  }}
                  renderValue={() => selectedMkbLabel || 'Выберите диагноз'}
                  MenuProps={{
                    ...searchMenuProps,
                    PaperProps: {
                      onScroll: handleMkbMenuScroll,
                      style: { maxHeight: 320, backgroundColor: '#fff' },
                    },
                  }}
                  fullWidth
                >
                  <ListSubheader
                    className={classes.stickySearchHeader}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <TextField
                      size='medium'
                      fullWidth
                      autoFocus
                      inputRef={mkbSearchInputRef}
                      value={mkbSearch}
                      placeholder='Поиск по МКБ'
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      onChange={(event) => setMkbSearch(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                      onKeyDown={stopSearchFieldKeys}
                    />
                  </ListSubheader>
                  {mkbOptions.map(option => (
                    <MenuItem key={option.id} value={option.id || ''}>
                      {option.code ? `${option.code} - ` : ''}{option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl variant='outlined' className={classes.field} fullWidth>
                <InputLabel>Клинический диагноз</InputLabel>
                <Select
                  label='Клинический диагноз'
                  value={selectedClinicalId}
                  onOpen={() => focusSearchInput(clinicalSearchInputRef)}
                  onChange={(event) => {
                    const id = Number(event.target.value)
                    const found = filteredClinical.find(option => Number(option.id) === id)
                    setSelectedClinicalId(id || '')
                    setSelectedClinicalLabel(found?.name || found?.value || '')
                  }}
                  renderValue={() => selectedClinicalLabel || 'Выберите диагноз'}
                  MenuProps={{
                    ...searchMenuProps,
                    PaperProps: {
                      style: { maxHeight: 320, backgroundColor: '#fff' },
                    },
                  }}
                  fullWidth
                >
                  <ListSubheader
                    className={classes.stickySearchHeader}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <TextField
                      size='medium'
                      fullWidth
                      autoFocus
                      inputRef={clinicalSearchInputRef}
                      value={clinicalSearch}
                      placeholder='Поиск клинического диагноза'
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      onChange={(event) => setClinicalSearch(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                      onKeyDown={stopSearchFieldKeys}
                    />
                  </ListSubheader>
                  {filteredClinical.map(option => (
                    <MenuItem key={option.id} value={option.id || ''}>
                      {option.name || option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              className={classes.addButton}
              variant='contained'
              color='primary'
              onClick={handleAddDiagnosis}
              disabled={!canAddDiagnosis}
            >
              Добавить диагноз
            </Button>
          </div>

          <Typography className={classes.diffSectionTitle}>Общие симптомы</Typography>
          <div className={classes.diffTabsWrap}>
            {availableDiffOptions.map(option => (
              <div
                key={option.id}
                className={`${classes.draggableCard} ${canDragSymptoms ? '' : classes.draggableCardDisabled}`}
                draggable={canDragSymptoms}
                onDragStart={(event) => {
                  if (!canDragSymptoms) {
                    event.preventDefault()
                    return
                  }
                  event.dataTransfer.setData('text/plain', String(option.id))
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
          <Typography className={classes.helperText}>
            {canDragSymptoms
              ? 'Перетащите нужный Симптом в одну из колонок.'
              : 'Чтобы перетаскивать общие симптомы, добавьте минимум два диагноза.'}
          </Typography>

          <div className={classes.columnsWrap}>
            {currentColumns.map(column => (
              <div
                key={column.id}
                className={`${classes.column} ${dragOverColumnId === column.id ? classes.columnDragOver : ''}`}
                onDragOver={(event) => {
                  if (!canDragSymptoms) return
                  event.preventDefault()
                  setDragOverColumnId(column.id)
                }}
                onDragLeave={() => setDragOverColumnId(null)}
                onDrop={(event) => {
                  event.preventDefault()
                  if (!canDragSymptoms) return
                  const draggedDiffId = Number(event.dataTransfer.getData('text/plain'))
                  if (!draggedDiffId) return
                  handleDropToColumn(column.id, draggedDiffId)
                }}
              >
                <Typography variant='body2' className={classes.columnTitle}>
                  <span>{column.diagnosisLabel}</span>
                  <IconButton size='small' onClick={() => removeDiagnosisColumn(column.id)} title='Удалить диагноз'>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Typography>

                {column.diffItems.map(diff => (
                  <div key={diff.id} className={classes.diffItem}>
                    <span>{diff.label}</span>
                    <div className={classes.actionsWrap}>
                      <IconButton size='small' disabled title='Правильный ответ'>
                        <CheckCircleOutlineIcon
                          fontSize='small'
                          style={{
                            color: showCheckResult && diff.exist === true ? '#2e7d32' : '#9e9e9e',
                          }}
                        />
                      </IconButton>
                      <IconButton size='small' disabled title='Неправильный ответ'>
                        <HighlightOffIcon
                          fontSize='small'
                          style={{
                            color: showCheckResult && diff.exist === false ? '#d32f2f' : '#9e9e9e',
                          }}
                        />
                      </IconButton>
                      <IconButton size='small' onClick={() => removeDiffFromColumn(column.id, diff.id)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} color='default'>
            Закрыть
          </Button>
          <Button variant='contained' color='primary' onClick={handleCheck} disabled={!canCheck}>
            Проверить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
